import React, { useEffect, useMemo, useState } from 'react';
import {
    ArrowRight,
    BookOpen,
    CalendarDays,
    CheckCircle,
    ClipboardCheck,
    Clock,
    FolderOpen,
    MessageSquareText,
    ShieldCheck,
    Sparkles,
    TrendingUp,
} from 'lucide-react';
import { teacherDashboardMockData } from '../data/teacherDashboardMockData';
import { operatingState as operatingStateApi } from '../services/api';
import {
    buildTeacherOperatingState,
    getPrimaryOperatingCourse as selectPrimaryOperatingCourse,
} from '../utils/teacherOperatingState';

const getPrimaryCourse = (courses = [], primaryCourseId) => {
    const explicitCourse = courses.find((course) => course.id === primaryCourseId);
    if (explicitCourse) return explicitCourse;

    return selectPrimaryOperatingCourse(courses, primaryCourseId);
};

const fillPrimaryClass = (template, primaryClass) => (
    template?.replace('{primaryClass}', primaryClass) || `${primaryClass} is ready for tomorrow.`
);

const getCadenceChip = (course) => {
    const unitLabel = course?.currentUnit || 'current unit';
    return `On pace for ${unitLabel}`;
};

const toneClasses = {
    teal: 'bg-white text-[#1d1d1f] border-black/5 dark:bg-slate-900/35 dark:text-slate-100 dark:border-slate-700/60',
    amber: 'bg-white text-[#1d1d1f] border-black/5 dark:bg-slate-900/35 dark:text-slate-100 dark:border-slate-700/60',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-100 dark:bg-emerald-400/12 dark:text-emerald-100 dark:border-emerald-400/25',
};

const HomeInfoCard = ({ title, icon, children }) => (
    <section className="rounded-3xl border border-white/80 bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,0.06)] dark:border-slate-700/60 dark:bg-[#171d26]">
        <div className="mb-4 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/80 text-[#1d1d1f] ring-1 ring-black/5 dark:bg-white/8 dark:text-slate-100 dark:ring-white/10">
                {React.createElement(icon, { size: 17 })}
            </div>
            <h3 className="text-sm font-black text-[#1d1d1f]">{title}</h3>
        </div>
        {children}
    </section>
);

const TodaySchedule = ({ items = [] }) => (
    <HomeInfoCard title="Today's schedule" icon={CalendarDays}>
        <div className="space-y-3">
            {items.slice(0, 4).map((item) => (
                <div key={item.id || `${item.time}-${item.label}`} className="flex gap-3">
                    <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/80 text-[#1d1d1f] ring-1 ring-black/5 dark:bg-white/8 dark:text-slate-100 dark:ring-white/10">
                        <Clock size={13} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-bold text-[#999]">{item.time}</p>
                        <p className="text-sm font-black text-[#1d1d1f]">{item.label}</p>
                        {item.detail && (
                            <p className="text-xs text-[#666]">{item.detail}</p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    </HomeInfoCard>
);

const ClassroomInsights = ({ insights = [] }) => (
    <HomeInfoCard title="Classroom insights" icon={TrendingUp}>
        <div className="space-y-3">
            {insights.slice(0, 3).map((insight) => (
                <div
                    key={insight.id}
                    className={`rounded-2xl border px-3 py-3 ${toneClasses[insight.tone] || toneClasses.teal}`}
                >
                    <div className="flex items-start gap-3">
                        <CheckCircle size={15} className="mt-0.5 shrink-0" />
                        <div>
                            <p className="text-xs font-black">{insight.label}</p>
                            <p className="mt-1 text-xs leading-relaxed">
                                <span className="font-black">{insight.value}</span> {insight.detail}
                            </p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </HomeInfoCard>
);

const Upcoming = ({ items = [] }) => (
    <HomeInfoCard title="Upcoming" icon={BookOpen}>
        <div className="space-y-3">
            {items.slice(0, 3).map((item) => (
                <div
                    key={item.id}
                    className="flex items-start gap-3 rounded-2xl border border-black/5 bg-[#F8FAF8] px-3 py-3 dark:border-slate-700/60 dark:bg-slate-900/35"
                >
                    <div className="w-10 shrink-0 rounded-xl bg-white px-2 py-1 text-center text-xs font-black text-[#1d1d1f] shadow-sm dark:bg-slate-900 dark:text-slate-100">
                        {item.day}
                    </div>
                    <div>
                        <p className="text-sm font-black text-[#1d1d1f]">{item.label}</p>
                        {item.detail && (
                            <p className="mt-0.5 text-xs text-[#666]">{item.detail}</p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    </HomeInfoCard>
);

const QuickActionStrip = ({ onOpenGradingStudio, onOpenResources }) => {
    const actions = [
        {
            id: 'grade-work',
            label: 'Grade work',
            detail: 'Open the existing grading workflow.',
            icon: ClipboardCheck,
            onClick: onOpenGradingStudio,
        },
        {
            id: 'find-resources',
            label: 'Find resources',
            detail: 'Go to the Resources workspace.',
            icon: FolderOpen,
            onClick: onOpenResources,
        },
        {
            id: 'view-pacing',
            label: 'View pacing',
            detail: 'Review pacing in today\'s Home cards.',
            icon: TrendingUp,
        },
    ];

    return (
        <section className="rounded-3xl border border-white/80 bg-white/80 p-4 shadow-sm dark:border-slate-700/60 dark:bg-[#171d26]/85">
            <h3 className="px-1 text-xs font-bold uppercase tracking-[0.18em] text-[#666]">
                Other things you can do
            </h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {actions.map((action) => {
                    const Icon = action.icon;
                    const className = 'min-h-[74px] rounded-2xl border border-black/5 bg-[#F8FAF8] px-3 py-3 text-left transition-all dark:border-slate-700/60 dark:bg-slate-900/35';

                    if (!action.onClick) {
                        return (
                            <div key={action.id} className={`${className} opacity-80`}>
                                <div className="flex items-start gap-2">
                                    <Icon size={15} className="mt-0.5 shrink-0 text-[#666]" />
                                    <div>
                                        <p className="text-sm font-black text-[#1d1d1f]">{action.label}</p>
                                        <p className="mt-1 text-xs leading-relaxed text-[#666]">{action.detail}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <button
                            key={action.id}
                            type="button"
                            onClick={action.onClick}
                            className={`${className} hover:border-black/10 hover:bg-white dark:hover:border-slate-500/60 dark:hover:bg-slate-800/70`}
                        >
                            <div className="flex items-start gap-2">
                                <Icon size={15} className="mt-0.5 shrink-0 text-[#1d1d1f] dark:text-slate-100" />
                                <div>
                                    <p className="text-sm font-black text-[#1d1d1f]">{action.label}</p>
                                    <p className="mt-1 text-xs leading-relaxed text-[#666]">{action.detail}</p>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

const CommunicationDraftPanel = ({ courses = [] }) => {
    const firstCourse = courses[0] || {};
    const [audience, setAudience] = useState('parent');
    const [courseId, setCourseId] = useState(firstCourse.id || '');
    const [situation, setSituation] = useState('');
    const [requestedAction, setRequestedAction] = useState('');
    const [draftResult, setDraftResult] = useState(null);
    const [error, setError] = useState('');
    const [isDrafting, setIsDrafting] = useState(false);

    useEffect(() => {
        if (!courseId && firstCourse.id) {
            setCourseId(firstCourse.id);
        }
    }, [courseId, firstCourse.id]);

    const selectedCourse = courses.find((course) => course.id === courseId) || firstCourse;
    const canDraft = situation.trim().length > 0 && !isDrafting;
    const draft = draftResult?.draft;
    const approval = draftResult?.approval || {};
    const privacyBoundary = draftResult?.privacyBoundary || {};

    const handleDraft = async () => {
        if (!canDraft) return;

        setIsDrafting(true);
        setError('');
        setDraftResult(null);

        try {
            const response = await operatingStateApi.createCommunicationDraft({
                audience,
                course_name: selectedCourse?.name || 'the course',
                student_ref: audience === 'admin' ? 'student-support-ref' : undefined,
                situation: situation.trim(),
                requested_action: requestedAction.trim() || 'confirm the next teacher-approved step',
            });
            setDraftResult(response.data);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                err?.message ||
                'Could not create a communication draft.'
            );
        } finally {
            setIsDrafting(false);
        }
    };

    return (
        <details className="rounded-3xl border border-white/80 bg-white/80 p-4 shadow-sm dark:border-slate-700/60 dark:bg-[#171d26]/85">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-1 py-1 marker:hidden">
                <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-[#1d1d1f] ring-1 ring-black/5 dark:bg-white/8 dark:text-slate-100 dark:ring-white/10">
                        <MessageSquareText size={17} />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-sm font-black text-[#1d1d1f]">Communication draft</h3>
                        <p className="mt-0.5 text-xs leading-relaxed text-[#666]">
                            Prepare a teacher-reviewed parent or admin note.
                        </p>
                    </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/12 dark:text-emerald-100">
                    <ShieldCheck size={13} />
                    Draft only
                </span>
            </summary>

            <div className="mt-4 grid gap-3 lg:grid-cols-[220px_1fr]">
                <div className="space-y-3">
                    <label className="block text-xs font-black text-[#666]">
                        Audience
                        <select
                            value={audience}
                            onChange={(event) => setAudience(event.target.value)}
                            className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 text-sm font-bold text-[#1d1d1f] outline-none focus:border-black/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-400"
                        >
                            <option value="parent">Parent</option>
                            <option value="admin">Admin</option>
                        </select>
                    </label>

                    <label className="block text-xs font-black text-[#666]">
                        Class
                        <select
                            value={courseId}
                            onChange={(event) => setCourseId(event.target.value)}
                            className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 text-sm font-bold text-[#1d1d1f] outline-none focus:border-black/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-400"
                        >
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.name}
                                </option>
                            ))}
                            {courses.length === 0 && (
                                <option value="">Current class</option>
                            )}
                        </select>
                    </label>
                </div>

                <div className="space-y-3">
                    <label className="block text-xs font-black text-[#666]">
                        Situation
                        <textarea
                            value={situation}
                            onChange={(event) => setSituation(event.target.value)}
                            rows={3}
                            placeholder="Student code eng-01 missed two checkpoints and needs a teacher-reviewed next step."
                            className="mt-1 w-full resize-none rounded-2xl border border-black/10 bg-white px-3 py-2 text-sm text-[#1d1d1f] outline-none focus:border-black/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-400"
                        />
                    </label>

                    <label className="block text-xs font-black text-[#666]">
                        Requested action
                        <input
                            type="text"
                            value={requestedAction}
                            onChange={(event) => setRequestedAction(event.target.value)}
                            placeholder="Review the checkpoint prompt before Friday."
                            className="mt-1 w-full rounded-2xl border border-black/10 bg-white px-3 py-2 text-sm text-[#1d1d1f] outline-none focus:border-black/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-slate-400"
                        />
                    </label>

                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={handleDraft}
                            disabled={!canDraft}
                            className="inline-flex items-center gap-2 rounded-2xl bg-[#1d1d1f] px-4 py-2 text-sm font-black text-white transition-all hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white"
                        >
                            {isDrafting ? 'Drafting...' : 'Draft'}
                        </button>
                        <span className="text-xs font-bold text-[#666]">
                            Teacher approval is required before any outreach.
                        </span>
                    </div>

                    {error && (
                        <p className="rounded-2xl border border-rose-100 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-800 dark:border-rose-400/25 dark:bg-rose-400/12 dark:text-rose-100">
                            {error}
                        </p>
                    )}

                    {draft && (
                        <div className="rounded-2xl border border-black/5 bg-[#F8FAF8] p-3 dark:border-slate-700/60 dark:bg-slate-900/35">
                            <div className="mb-3 flex flex-wrap gap-2">
                                {approval.teacherApprovalRequired && (
                                    <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-800 dark:border-emerald-400/25 dark:bg-emerald-400/12 dark:text-emerald-100">
                                        Teacher approval required
                                    </span>
                                )}
                                {approval.sendAutomationAllowed === false && (
                                    <span className="rounded-full border border-amber-100 bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-800 dark:border-amber-400/25 dark:bg-amber-400/12 dark:text-amber-100">
                                        Send automation blocked
                                    </span>
                                )}
                                {privacyBoundary.redactionApplied && (
                                    <span className="rounded-full border border-sky-100 bg-sky-50 px-2.5 py-1 text-xs font-black text-sky-800 dark:border-sky-400/25 dark:bg-sky-400/12 dark:text-sky-100">
                                        Redaction applied
                                    </span>
                                )}
                            </div>
                            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#666]">Subject</p>
                            <p className="mt-1 text-sm font-black text-[#1d1d1f]">{draft.subject}</p>
                            <p className="mt-3 text-xs font-black uppercase tracking-[0.16em] text-[#666]">Body</p>
                            <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded-2xl bg-white p-3 text-xs leading-relaxed text-[#1d1d1f] dark:bg-slate-950 dark:text-slate-100">
                                {draft.body}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </details>
    );
};

const TeacherOperatingCenter = ({
    operatingState,
    onGenerateLesson,
    onOpenGradingStudio,
    onOpenResources,
}) => {
    const data = useMemo(() => {
        const resolvedState =
            operatingState || buildTeacherOperatingState({ fallbackData: teacherDashboardMockData });

        return {
            ...teacherDashboardMockData,
            ...resolvedState,
            homeHero: resolvedState.homeHero || teacherDashboardMockData.homeHero,
            todaySchedule: resolvedState.todaySchedule || teacherDashboardMockData.todaySchedule,
            classroomInsights: resolvedState.classroomInsights || teacherDashboardMockData.classroomInsights,
            upcoming: resolvedState.upcoming || teacherDashboardMockData.upcoming,
            watchAheadNote: resolvedState.watchAheadNote || teacherDashboardMockData.watchAheadNote,
        };
    }, [operatingState]);
    const primaryCourse = getPrimaryCourse(data.courses, data.operatingBrief?.primaryCourseId);
    const primaryClass = primaryCourse?.name || 'Engineering';
    const contextChips = [
        getCadenceChip(primaryCourse),
        ...(data.homeHero?.contextChips || []),
    ].filter(Boolean).slice(0, 2);

    return (
        <section className="space-y-5" aria-label="Teacher Home">
            <section className="overflow-hidden rounded-[32px] border border-white/80 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)] dark:border-slate-700/60 dark:bg-[#171d26]">
                <div className="grid gap-5 p-5 lg:grid-cols-[1fr_360px] lg:p-6">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">
                            {data.homeHero?.label || 'Your next step'}
                        </p>
                        <h2 className="mt-3 max-w-2xl text-2xl font-black tracking-tight text-[#1d1d1f] sm:text-3xl">
                            {data.homeHero?.title || 'Generate tomorrow\'s lesson'}
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#444]">
                            {data.homeHero?.explanation}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2">
                            {contextChips.map((chip) => (
                                <span
                                    key={chip}
                                    className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-bold text-[#1d1d1f] dark:border-slate-700 dark:bg-slate-900/35 dark:text-slate-100"
                                >
                                    {chip}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl bg-[#f5f5f7] p-4 dark:bg-slate-900/45">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#666]">
                            Ready class
                        </p>
                        <p className="mt-2 text-xl font-black text-[#1d1d1f]">
                            {fillPrimaryClass(data.homeHero?.readyMessage, primaryClass)}
                        </p>
                        <button
                            type="button"
                            onClick={() => onGenerateLesson?.(primaryCourse)}
                            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#FFC107] px-4 py-3 text-sm font-black text-[#1d1d1f] shadow-[0_12px_30px_rgba(255,193,7,0.28)] transition-all hover:bg-[#e6ad00] active:scale-[0.99] dark:bg-amber-400 dark:text-slate-950 dark:hover:bg-amber-300"
                        >
                            Generate lesson
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </section>

            <div className="grid gap-4 xl:grid-cols-3">
                <TodaySchedule items={data.todaySchedule} />
                <ClassroomInsights insights={data.classroomInsights} />
                <Upcoming items={data.upcoming} />
            </div>

            <QuickActionStrip
                onOpenGradingStudio={onOpenGradingStudio}
                onOpenResources={onOpenResources}
            />

            <CommunicationDraftPanel courses={data.courses} />

            <section className="rounded-3xl border border-white/80 bg-white/80 px-4 py-3 text-sm text-[#1d1d1f] dark:border-slate-700/60 dark:bg-[#171d26]/85 dark:text-slate-100">
                <div className="flex items-start gap-2">
                    <Sparkles size={16} className="mt-0.5 shrink-0" />
                    <p>{data.watchAheadNote?.text}</p>
                </div>
            </section>
        </section>
    );
};

export default TeacherOperatingCenter;
